import { useEffect } from 'react';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import type { UserEmailInfo } from '@/features/auth/types/auth.types';

/**
 * useUserGuiding
 *
 * Identifies the authenticated user in UserGuiding for guided tours and onboarding.
 * Automatically calls `window.userGuiding.identify` when user info becomes available.
 *
 * ## Features
 * - **Auto-identification**: Triggers when user data is fetched from the API
 * - **Safe loading**: Guards against the UserGuiding SDK not being loaded yet
 * - **Email normalization**: Handles both `string` and `UserEmailInfo` email formats
 * - **Segmentation attributes**: Sends `company`, `entity`, and `profile` for
 *   guided-tour targeting
 * - **Re-identification**: Runs again if user info or active entity changes
 *
 * ## Attributes sent to UserGuiding
 * - `company` → `userInfo.businessAccount.name` (active company)
 * - `entity` → name of the currently selected platform (from `useWorkspace`)
 * - `profile` → `userInfo.roles` (user roles)
 *
 * ## Integration Flow
 * ```mermaid
 * flowchart LR
 *   A[useUserInfo] --> B{userInfo?}
 *   B -->|Yes| C{SDK loaded?}
 *   C -->|Yes| D[identify user]
 *   B -->|No| E[skip]
 *   C -->|No| E
 * ```
 *
 * @returns void — side-effect only hook
 *
 * @example
 * ```tsx
 * const HomeLayout: React.FC = () => {
 *   useUserGuiding();
 *   return <Outlet />;
 * };
 * ```
 *
 * @since 1.0.0
 * @category Hooks
 */
export function useUserGuiding(): void {
  const { data: userInfo } = useUserInfo();
  const { entities, selectedEntityId } = useWorkspace();

  useEffect(() => {
    if (!userInfo || !window.userGuiding) return;

    const email =
      typeof userInfo.email === 'string'
        ? userInfo.email
        : (userInfo.email as UserEmailInfo).email;

    const entity = entities.find((e) => e.id === selectedEntityId)?.name;

    window.userGuiding.identify(userInfo.userId, {
      email,
      name: userInfo.name,
      company: userInfo.businessAccount?.name,
      entity,
      profile: userInfo.roles,
    });
  }, [userInfo, entities, selectedEntityId]);
}
